import { api } from './api';

// Types
export type IssueType =
  | 'REPEAT_SERVICE'
  | 'EXCESSIVE_REFUND'
  | 'NO_INVOICE'
  | 'NOT_READY_RETURN'
  | 'CANCEL_AFTER_WORK'
  | 'QUALITY_ISSUE'
  | 'REVENUE_LOSS';

export type IssueSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type IssueStatus = 'OPEN' | 'UNDER_REVIEW' | 'RESOLVED' | 'DISMISSED';

export interface TechnicianIssue {
  id: string;
  technicianId: string;
  branchId: string;
  companyId: string;
  issueType: IssueType;
  severity: IssueSeverity;
  title: string;
  description: string;
  serviceId?: string;
  revenueImpact?: number;
  status: IssueStatus;
  resolvedAt?: string;
  resolvedById?: string;
  resolution?: string;
  isAutoDetected: boolean;
  createdAt: string;
  updatedAt: string;
  technician?: {
    id: string;
    user: { name: string };
    currentLevel?: { name: string; sortOrder: number };
  };
  service?: {
    id: string;
    ticketNumber: string;
    deviceModel: string;
    status: string;
    customer?: { name: string; phone: string };
  };
}

export interface TechnicianPerformance {
  technicianId: string;
  technicianName: string;
  technicianPhone?: string;
  branchName: string;
  branchCode: string;
  levelName: string;
  sortOrder: number;
  // Points
  totalPoints: number;
  // Positive metrics
  totalServices: number;
  completedServices: number;
  deliveredServices: number;
  totalRevenue: number;
  avgRating: number | null;
  performanceScore: number;
  // Status breakdown
  pendingServices: number;
  inProgressServices: number;
  waitingPartsServices: number;
  readyServices: number;
  // Negative metrics
  repeatServices: number;
  refundsCount: number;
  noInvoiceServices: number;
  notReadyReturns: number;
  // Issues
  openIssues: number;
  criticalIssues: number;
  hasIssues: boolean;
}

export interface IssueStats {
  total: number;
  open: number;
  critical: number;
  highSeverity: number;
  byType: Record<string, number>;
}

export interface IssueFilters {
  branchId?: string;
  technicianId?: string;
  issueType?: IssueType;
  status?: IssueStatus;
  severity?: IssueSeverity;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface PerformanceFilters {
  branchId?: string;
  year?: number;
  month?: number;
  page?: number;
  limit?: number;
}

// Helper functions
export const ISSUE_TYPE_LABELS: Record<IssueType, string> = {
  REPEAT_SERVICE: 'Repeat Service',
  EXCESSIVE_REFUND: 'Excessive Refunds',
  NO_INVOICE: 'No Invoice Created',
  NOT_READY_RETURN: 'Not Ready Return',
  CANCEL_AFTER_WORK: 'Cancelled After Work',
  QUALITY_ISSUE: 'Quality Complaint',
  REVENUE_LOSS: 'Revenue Loss Pattern',
};

export const SEVERITY_COLORS: Record<IssueSeverity, string> = {
  LOW: 'bg-gray-100 text-gray-700 border-gray-200',
  MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  HIGH: 'bg-orange-100 text-orange-700 border-orange-200',
  CRITICAL: 'bg-red-100 text-red-700 border-red-200',
};

export const STATUS_COLORS: Record<IssueStatus, string> = {
  OPEN: 'bg-red-100 text-red-700',
  UNDER_REVIEW: 'bg-yellow-100 text-yellow-700',
  RESOLVED: 'bg-green-100 text-green-700',
  DISMISSED: 'bg-gray-100 text-gray-700',
};

export const getScoreColor = (score: number): string => {
  if (score === 0) return 'text-gray-400';
  if (score >= 90) return 'text-green-600';
  if (score >= 75) return 'text-blue-600';
  if (score >= 60) return 'text-yellow-600';
  if (score >= 40) return 'text-orange-600';
  return 'text-red-600';
};

export const getScoreLabel = (score: number): string => {
  if (score === 0) return 'N/A';
  if (score >= 90) return 'Excellent';
  if (score >= 75) return 'Good';
  if (score >= 60) return 'Average';
  if (score >= 40) return 'Needs Improvement';
  return 'Poor';
};

export const getScoreBgColor = (score: number): string => {
  if (score >= 90) return 'bg-green-500';
  if (score >= 75) return 'bg-blue-500';
  if (score >= 60) return 'bg-yellow-500';
  if (score >= 40) return 'bg-orange-500';
  return 'bg-red-500';
};

// API methods
export const technicianPerformanceApi = {
  // ==================== Issues ====================

  // Get issue statistics
  getIssueStats: async (branchId?: string): Promise<IssueStats> => {
    const params = new URLSearchParams();
    if (branchId) params.append('branchId', branchId);
    const response = await api.get(`/technician-performance/issues/stats?${params.toString()}`);
    return response.data.data.stats;
  },

  // Get issues with filters
  getIssues: async (filters: IssueFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.branchId) params.append('branchId', filters.branchId);
    if (filters.technicianId) params.append('technicianId', filters.technicianId);
    if (filters.issueType) params.append('issueType', filters.issueType);
    if (filters.status) params.append('status', filters.status);
    if (filters.severity) params.append('severity', filters.severity);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await api.get(`/technician-performance/issues?${params.toString()}`);
    return response.data.data;
  },

  // Get issue by ID
  getIssueById: async (id: string): Promise<TechnicianIssue> => {
    const response = await api.get(`/technician-performance/issues/${id}`);
    return response.data.data.issue;
  },

  // Create a new issue
  createIssue: async (data: {
    technicianId: string;
    branchId: string;
    issueType: IssueType;
    severity?: IssueSeverity;
    title: string;
    description: string;
    serviceId?: string;
    revenueImpact?: number;
  }): Promise<TechnicianIssue> => {
    const response = await api.post('/technician-performance/issues', data);
    return response.data.data.issue;
  },

  // Resolve an issue
  resolveIssue: async (id: string, resolution: string, status: 'RESOLVED' | 'DISMISSED' = 'RESOLVED') => {
    const response = await api.put(`/technician-performance/issues/${id}/resolve`, { resolution, status });
    return response.data.data.issue;
  },

  // Update issue status
  updateIssueStatus: async (id: string, status: IssueStatus) => {
    const response = await api.put(`/technician-performance/issues/${id}/status`, { status });
    return response.data.data.issue;
  },

  // ==================== Performance ====================

  // Get performance dashboard
  getPerformanceDashboard: async (filters: PerformanceFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.branchId) params.append('branchId', filters.branchId);
    if (filters.year) params.append('year', filters.year.toString());
    if (filters.month) params.append('month', filters.month.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await api.get(`/technician-performance/dashboard?${params.toString()}`);
    return response.data.data;
  },

  // Get performance for specific technician
  getTechnicianPerformance: async (technicianId: string, year?: number) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());

    const response = await api.get(`/technician-performance/technician/${technicianId}?${params.toString()}`);
    return response.data.data;
  },

  // Calculate performance for a technician
  calculatePerformance: async (technicianId: string, year?: number, month?: number) => {
    const response = await api.post(`/technician-performance/calculate/${technicianId}`, { year, month });
    return response.data.data.performance;
  },

  // Calculate performance for all technicians
  calculateAllPerformance: async (branchId?: string, year?: number, month?: number) => {
    const response = await api.post('/technician-performance/calculate-all', { branchId, year, month });
    return response.data.data;
  },
};
